/**
 * Text utility functions.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright (c) Ircam amplify 2024
 */

import { Util } from './Util'

export enum CurrencyPlace {
  Before = 'before',
  After = 'after',
}

export enum DateType {
  DayMonth,
  DayMonthYear,
}

export const TextUtil = {
  /**
   * Capitalize the first character of a string.
   */
  capitalizeFirstChar: (str: string | undefined): string | undefined => {
    /**
     * Capitalize the first character of the given string.
     *
     * @param str
     *  The string to transform.
     * @returns
     *  The transformed string.
     */
    if (!str) {
      return undefined
    }

    return str.charAt(0).toUpperCase() + str.slice(1)
  },

  /**
   * Capitalize the first character of each word in a string.
   */
  capitalizeWordFirstChar: (str: string | undefined): string | undefined => {
    /**
     * Capitalize the first letter of each word.
     *
     * @param str
     *  The string to transform.
     * @returns
     *  The transformed string.
     */
    if (!str) {
      return undefined
    }

    return str.replace(/\b\w/g, (char) => char.toUpperCase())
  },

  /**
   * Compare two strings.
   * @param str1 The first string.
   * @param str2 The second string.
   * @param isCaseSensitive True if the comparison must be case-sensitive.
   * @returns True if the two strings are the same.
   */
  strCompare: (
    str1: string,
    str2: string,
    isCaseSensitive: boolean = true
  ): boolean => {
    if (isCaseSensitive) {
      return str1 === str2
    } else {
      return str1.toLowerCase() === str2.toLowerCase()
    }
  },

  /**
   * Format a number of seconds to a string like 3h54m21s.
   *
   * @param seconds The number of seconds.
   * @returns The formatted string.
   */
  formatDuration: (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60

    const parts: string[] = []

    if (hours > 0) parts.push(`${hours}h`)

    if (minutes > 0)
      parts.push(
        parts.length === 0
          ? `${minutes}m`
          : `${String(minutes).padStart(2, '0')}m`
      )

    if (remainingSeconds > 0)
      parts.push(
        parts.length === 0
          ? `${remainingSeconds}s`
          : `${String(remainingSeconds).padStart(2, '0')}s`
      )

    return parts.join('')
  },

  /**
   * Returns 'are' or 'is' depending on the number passed as argument.
   */
  is_or_are: (number: number): string => {
    return number > 1 ? 'are' : 'is'
  },

  /**
   * Convert a string to a number if its contains a number value, else return
   * the string itself.
   */
  toNumber: (strValue: string): any => {
    const value = Number(strValue)
    if (Number.isNaN(value)) {
      return strValue
    }

    return value
  },

  /**
   * Convert the argument to a number, a boolean, a string, null or undefined
   * whatever is its type. If the argument is a stringified number, boolean,
   * null or undefined, the value is converted to its type.
   *
   * Example:
   *   normalizeValue(12) returns 12.
   *   normalizeValue('12') returns 12.
   *   normalizeValue('false') returns the boolean false.
   *   normalizeValue(true) returns the boolean true.
   *   normalizeValue('null') returns null.
   *   normalizeValue('one string') returns the 'one string'.
   */
  normalizeValue: (
    input: unknown
  ): number | boolean | string | null | undefined => {
    if (typeof input === 'number') {
      return input
    }

    if (typeof input === 'boolean') {
      return input
    }

    if (typeof input === 'string') {
      const lower = input.toLowerCase()

      if (lower === 'true') return true
      if (lower === 'false') return false
      if (lower === 'null') return null
      if (lower === 'undefined') return undefined

      const num = Number(input)
      if (!isNaN(num)) return num

      return input
    }

    return String(input)
  },

  getOrdinalNumber(n: number): string {
    if (n > 3 && n < 21) return 'th'
    switch (n % 10) {
      case 1:
        return 'st'
      case 2:
        return 'nd'
      case 3:
        return 'rd'
      default:
        return 'th'
    }
  },

  /**
   * Format a Date instance to a string with ordinal number.
   *
   * @param date The Date instance.
   * @param dateType The type of requested date.
   * @returns A formatted string.
   */
  dateToMonthDayWithOrdinal(
    date: Date,
    dateType: DateType = DateType.DayMonth
  ): string {
    const day = date.getDate()
    const month = date.toLocaleString(Util.getLocale(), { month: 'long' })
    const strDate = `${month} ${day}${TextUtil.getOrdinalNumber(day)}`

    if (DateType.DayMonthYear === dateType) {
      return `${strDate}, ${date.getFullYear}`
    } else {
      return strDate
    }
  },

  /**
   * Return the symbol of a currency given its string version.
   */
  currencySymbol(currency: string | undefined): [string, CurrencyPlace] {
    switch (currency) {
      case 'eur':
      case 'euro':
        return ['€', CurrencyPlace.After]

      case 'dollar':
      case '$':
        return ['$', CurrencyPlace.Before]

      default:
        return ['', CurrencyPlace.After]
    }
  },

  /**
   * Format a string for a price.
   *
   * @param price
   *    The value of the price.
   * @param currencySymbol
   *    The symbol of the currency: $ or €
   * @param isFixed2
   *    If true, always format with 2-digit fixed point,
   *    even if decimal part is null.
   * @returns
   *    The formatted string.
   */
  formatPrice(
    price: number,
    currencySymbol: string,
    isFixed2?: boolean
  ): string {
    const value = isFixed2
      ? Util.ceilTo2Digits(price).toFixed(2)
      : Math.ceil(price)
    return '$' == currencySymbol
      ? `${currencySymbol}${value}`
      : `${value}${currencySymbol}`
  },

  /**
   * Convert a string containing lines to a <div> containing <p> components.
   * @param msg The message containing lines, separated by '\n'.
   * @returns A <div> component.
   */
  linesToDiv(msg: string) {
    const lines = msg.split('\n')
    return (
      <div>
        {lines.map((line) => (
          <p>{line}</p>
        ))}
      </div>
    )
  },

  /**
   * Replace the spaces of a given string by the given pattern.
   * @param input The string to replace the spaces.
   * @param by The pattern replacing the spaces.
   * @returns A new string.
   */
  replaceSpaces(input: string, by: string = '-'): string {
    if (input) {
      return input.replace(/\s+/g, by)
    } else {
      return ''
    }
  },
}
